import useSWR from 'swr'

import { useAuth } from './contexts/AuthContext'

const RETRY_BASE_DELAY_MS = 5_000
const MAX_RETRY_DELAY_MS = 60_000

class AccessTokenError extends Error {
  constructor(readonly status: number) {
    super(`Failed to fetch access token: ${status}`)
  }
}

type LayerAccessToken = {
  access_token: string
  expires_in: number
}

export const useLayerAccessToken = (url: string | null) => {
  const { clientName, authFetch } = useAuth()

  return useSWR(
    url && clientName ? ['layer-access-token', url, clientName] : null,
    async ([, url]): Promise<LayerAccessToken> => {
      const response = await authFetch(url, { method: 'POST' })
      if (!response.ok) throw new AccessTokenError(response.status)
      return response.json()
    },
    {
      refreshInterval: latestData => latestData ? (latestData.expires_in * 1000) / 2 : 0,
      revalidateOnFocus: false,
      onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
        if (error instanceof AccessTokenError && error.status < 500) return
        setTimeout(
          () => revalidate({ retryCount }),
          Math.min(RETRY_BASE_DELAY_MS * 2 ** retryCount, MAX_RETRY_DELAY_MS),
        )
      },
    },
  )
}
